import { Box, FormHelperText, Grid, Typography } from "@mui/material"

import { FormikSwitch, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"
import { WorkgroupsMembershipPanel } from "../../../../../../shared/components/forms/WorkgroupsMembershipPanel"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { History } from "../../../../../users/userTypes"

import { TenantUserFormValues } from "../tenantUserTypes"

type TenantUserFormContentProps = Readonly<{
    isEditMode: boolean
    history?: History
}>

export function TenantUserFormContent({ isEditMode, history }: TenantUserFormContentProps) {
    const dateTimeFactory = useDateTime()

    return (
        <StandardForm>
            <StandardMainPanel>
                <LocalizedStrict id="office-manager-create-user-field-id" attrs={{ label: true }}>
                    <FormikTextField<TenantUserFormValues> name="employeeId" label="Employee ID" fullWidth />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-name" attrs={{ label: true }}>
                    <FormikTextField<TenantUserFormValues>
                        required
                        name="displayName"
                        label="Name"
                        autoComplete="name"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-email" attrs={{ label: true }}>
                    <FormikTextField<TenantUserFormValues>
                        required
                        name="email"
                        label="Email"
                        autoComplete="email"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-mobile" attrs={{ label: true }}>
                    <FormikTextField<TenantUserFormValues>
                        name="mobileNumber"
                        label="Mobile"
                        autoComplete="mobile"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-phone" attrs={{ label: true }}>
                    <FormikTextField<TenantUserFormValues> name="phoneNumber" label="Phone no." fullWidth />
                </LocalizedStrict>
            </StandardMainPanel>
            <LocalizedStrict id="office-manager-settings-panel" attrs={{ title: true }}>
                <StandardSidePanel title="Settings">
                    {isEditMode && (
                        <LocalizedStrict id="office-manager-create-user-field-active">
                            <FormikSwitch<TenantUserFormValues> name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                    )}
                    <LocalizedStrict id="office-manager-settings-send-invitation">
                        <FormikSwitch<TenantUserFormValues> name="sendEmailInvitation">
                            Send email invitation
                        </FormikSwitch>
                    </LocalizedStrict>
                    <Box>
                        <LocalizedStrict id="office-manager-settings-disable-sync">
                            <FormikSwitch<TenantUserFormValues> name="ignoreUserSyncJobs">
                                Disable synchronization
                            </FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-settings-disable-sync-text">
                            <FormHelperText>
                                When disabled this user will be ignored by user synchronization jobs
                            </FormHelperText>
                        </LocalizedStrict>
                    </Box>
                </StandardSidePanel>
            </LocalizedStrict>

            <LocalizedStrict id="company-user-workgroups-panel" attrs={{ title: true }}>
                <StandardSidePanel title="Workgroups">
                    <WorkgroupsMembershipPanel />
                </StandardSidePanel>
            </LocalizedStrict>

            {history !== undefined && (
                <LocalizedStrict id="history-card" attrs={{ title: true }}>
                    <StandardSidePanel title="history">
                        <Grid container rowGap={3}>
                            <Grid container item>
                                <Grid item xs={12} sm={5} justifyContent="space-between">
                                    <LocalizedStrict id="history-created">
                                        <Typography variant="body2">Created:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {dateTimeFactory.formatDateTime(
                                            dateTimeFactory.fromApi(history.created.timestamp),
                                        )}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {history.created.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container item>
                                <Grid item xs={12} sm={5}>
                                    <LocalizedStrict id="history-edited">
                                        <Typography variant="body2">Edited:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {history.updated
                                            ? dateTimeFactory.formatDateTime(
                                                  dateTimeFactory.fromApi(history.updated.timestamp),
                                              )
                                            : "-"}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {history.updated?.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </StandardSidePanel>
                </LocalizedStrict>
            )}
        </StandardForm>
    )
}
